/**
 * C-07 «Чат» и C-21 «Закрытый диалог».
 *
 * ПС §11: шапка с собеседником, история, поле ввода, вложения, голосовая
 * запись, статус соединения при проблемах. После block/unmatch поле отправки
 * отключается, а история остаётся доступной для чтения (решение D-06).
 */
import { useNavigate, useParams, useRouter, useRouterState } from '@tanstack/react-router'
import { lazy, useCallback, useEffect, useLayoutEffect, useRef, useState, Suspense } from 'react'

import { faceOf, spaceIcon, subscribersLabel } from '@entities/conversation/display'
import { useConversation, useMessages } from '@entities/conversation/model'
import { useChannelMembers } from '@features/channels/use-channel-members'
import { useChatActions } from '@features/chat/use-chat-actions'
import { useReadReceipts } from '@features/chat/use-read-receipts'
import type { Message } from '@shared/api/types'
import type { LocalMediaDraft, LocalMessage } from '@shared/db'
import { UNCONFIRMED_STATES, uiStateRepo, UiKey } from '@shared/db'
import { ReportDialog, type ReportTarget } from '@features/report/ReportDialog'
import type { ParsedLink } from '@shared/lib/links'
import { canDeleteForAll, canEditMessage } from '@shared/lib/message-rules'
import {
  ActionItem,
  ActionList,
  Avatar,
  Button,
  Dialog,
  EmptyState,
  Icon,
  Sheet,
  Spinner,
  cx,
  useToast,
} from '@shared/ui'

import { hotkeysAvailable } from '../../model/hotkeys'
import { hasInAppHistory } from '../../model/navigation-direction'
import { ScreenFrame, type ScreenFrameHandle } from '../../layouts/ScreenFrame'
import { rememberHistoryAnchor, takeHistoryAnchor } from '../../model/scroll-memory'
import { setActiveConversation } from '../../boot/active-chat'
import { selectIsReadOnly, useAppStore } from '../../model/app-store'
import { offerPushPrompt } from '../../model/push-prompt'
import { useTyping } from '../../model/typing-store'
import { formatPresence } from '../../lib/format'
import { Composer, type ComposerMode } from './Composer'
import { ChatSearch } from './ChatSearch'
import { ForwardSheet } from './ForwardSheet'
import { ExternalLinkDialog } from './ExternalLinkDialog'
import { MessageHistory, type MessageHistoryHandle } from './MessageHistory'
import { mediaLabel } from './message-text'
import { MUTE_OPTIONS } from './mute-options'
import styles from './message.module.css'

/*
 * Полоса канала нужна только на ленте канала — отдельный чанк, чтобы не занимать бюджет
 * маршрута переписки (§11.1).
 */
const ChannelFooter = lazy(async () => ({
  default: (await import('../channels/ChannelFooter')).ChannelFooter,
}))

/*
 * Просмотрщик вложений открывают нечасто, а весит он много (жесты, зум, свайпы) — отдельный
 * чанк, чтобы не занимать бюджет маршрута переписки (§11.1).
 */
const MediaViewer = lazy(async () => ({
  default: (await import('./MediaViewer')).MediaViewer,
}))

/**
 * SF-02: к жалобе на сообщение прикладывается контекст переписки — соседние
 * сообщения вокруг спорного, чтобы решение принималось по разговору.
 */
const REPORT_CONTEXT_RADIUS = 5

function messageReportTarget(
  message: LocalMessage,
  history: readonly LocalMessage[],
  counterpartName: string | null,
): ReportTarget {
  const index = history.findIndex((item) => item.id === message.id)
  const from = Math.max(0, index - REPORT_CONTEXT_RADIUS)
  const context = index < 0 ? [] : history.slice(from, index + REPORT_CONTEXT_RADIUS + 1)

  return {
    type: 'message',
    id: message.id,
    title: 'Пожаловаться на сообщение',
    subject: counterpartName,
    // Неподтверждённые записи серверу неизвестны — их идентификаторы локальные.
    contextMessageIds: context.filter((item) => !item.id.startsWith('local:')).map((item) => item.id),
  }
}

interface ChatScreenProps {
  /** В двухпанельном режиме id приходит снаружи, а не из маршрута. */
  conversationIdOverride?: string
  embedded?: boolean
}

export function ChatScreen({ conversationIdOverride, embedded = false }: ChatScreenProps = {}) {
  const params = useParams({ strict: false }) as { conversationId?: string }
  const conversationId = conversationIdOverride ?? params.conversationId ?? null

  const navigate = useNavigate()
  const router = useRouter()
  const toast = useToast()
  const actions = useChatActions()
  const frameRef = useRef<ScreenFrameHandle>(null)

  const me = useAppStore((s) => s.me)
  const config = useAppStore((s) => s.config)
  // BS §6.3: санкция `read_only` / `banned` запрещает отправку (SYS-16).
  const readOnly = useAppStore(selectIsReadOnly)
  const myUserId = me?.user.id ?? ''

  const conversation = useConversation(conversationId)
  const pinned = conversation?.pinned_message ?? null
  const { items: messages, loaded: messagesLoaded } = useMessages(conversationId)

  /*
   * Изменение v1.10: в группе над чужим сообщением стоит имя автора, а слева — его аватар.
   * Безопасные карточки приходят вместе с историей: список участников доступен только
   * администраторам. В канале посты идут от самого канала и подпись человека скрыта.
   */
  const groupCard =
    conversation?.kind === 'channel' && conversation.channel?.format === 'group' ? conversation.channel : null
  const senders = useChannelMembers(messages)

  const [composerMode, setComposerMode] = useState<ComposerMode>({ kind: 'new' })
  const pendingForward = useAppStore((s) => s.pendingForward)
  const setPendingForward = useAppStore((s) => s.setPendingForward)
  const [menuFor, setMenuFor] = useState<{ message: LocalMessage; anchor: DOMRect } | null>(null)
  const [chatMenuAnchor, setChatMenuAnchor] = useState<DOMRect | null>(null)
  const [highlightSeq, setHighlightSeq] = useState<number | null>(null)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [viewerMediaId, setViewerMediaId] = useState<string | null>(null)
  // C-22: ссылка, для которой показано предупреждение.
  const [pendingLink, setPendingLink] = useState<ParsedLink | null>(null)
  const [muteOpen, setMuteOpen] = useState(false)
  const [confirm, setConfirm] = useState<'clear' | 'hide' | null>(null)
  // C-08 доступен и по deep link `/chats/{id}/search`.
  const searchRoute = useRouterState({ select: (state) => state.location.pathname.endsWith('/search') })
  const [searchOpen, setSearchOpen] = useState(searchRoute)
  const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null)
  /** Сообщение, которое пересылают (лист выбора диалога). */
  const [forwardFor, setForwardFor] = useState<LocalMessage | null>(null)
  /** Закрепить и переслать можно только сообщение с серверным id — не из очереди отправки. */
  const menuConfirmed = menuFor ? !UNCONFIRMED_STATES.includes(menuFor.message.local_state) : false
  // C-25: подсветка области чата при перетаскивании файла.
  const [dragActive, setDragActive] = useState(false)
  const [droppedFile, setDroppedFile] = useState<File | null>(null)
  const dragDepthRef = useRef(0)

  const historyHandleRef = useRef<MessageHistoryHandle>(null)
  const restoredRef = useRef(false)
  /*
   * Граница непрочитанного фиксируется при открытии диалога: `markRead` ниже
   * почти сразу обнулит счётчик в реплике, а плашка «Новые сообщения» должна
   * остаться на месте, пока диалог открыт.
   */
  const firstUnreadRef = useRef<{ conversationId: string; seq: number | null } | null>(null)

  /*
   * На mobile диалог — вложенный экран (ScreenFrame): входит «толчком» и уходит
   * кнопкой или жестом от края. «Назад» ведёт историей — туда, откуда диалог
   * открыли (список, профиль, уведомление); по deep link истории нет — в список.
   */
  const navigateBack = useCallback((): void => {
    if (hasInAppHistory()) router.history.back()
    else void navigate({ to: '/chats', replace: true })
  }, [navigate, router])
  const goBack = (): void => {
    if (frameRef.current) frameRef.current.pop(navigateBack)
    else navigateBack()
  }

  /*
   * §3.4 п. 5 / IOS §3.2: под экранной клавиатурой вся раскладка сжимается до
   * видимой области (app/model/viewport.ts) — композер остаётся над клавиатурой
   * без отдельного отступа. Якорь истории по `seq` при изменении высоты держит сама лента.
   */

  /* --- Подсказка подавления push (BS §13.3, CC §4.5) ------------------- */

  useEffect(() => {
    if (!conversationId) return

    // Зеркало обслуживает и кадр `active_chat`, и запись для service worker'а,
    // и снятие показанных уведомлений серии (§8.5).
    setActiveConversation(conversationId)

    // §8.1 п. 2: вторая точка входа в N-03 — первое открытие чата.
    void offerPushPrompt()

    return () => setActiveConversation(null)
  }, [conversationId])

  /* --- Режим композера при смене диалога --------------------------------- */

  /*
   * Диалог-цель пересылки открывается с композером в режиме «переслать»;
   * любой другой переход снимает режим, привязанный к сообщению прежнего
   * диалога. Один эффект, а не два: порядок двух эффектов на монтировании
   * сбрасывал бы только что выставленный режим пересылки.
   */
  const modeForRef = useRef<string | null>(null)
  useEffect(() => {
    if (!conversationId) return

    if (pendingForward?.targetConversationId === conversationId) {
      modeForRef.current = conversationId
      setComposerMode({ kind: 'forward', message: pendingForward.message })
      setPendingForward(null)
      return
    }

    if (modeForRef.current !== conversationId) {
      modeForRef.current = conversationId
      setComposerMode({ kind: 'new' })
    }
  }, [conversationId, pendingForward, setPendingForward])

  /* --- Индикатор набора собеседника ------------------------------------- */

  const typing = useTyping(conversationId)

  /* --- Первичная загрузка истории ----------------------------------------- */

  /*
   * Реплика хранит списки диалогов, но не сообщения: у диалога с историей
   * локальный список пуст, пока не придёт первая страница. Пока она не пришла,
   * «Начните разговор» не показывается — иначе карточка мелькала на кадр при
   * каждом открытии.
   */
  const [historyReady, setHistoryReady] = useState(false)
  useEffect(() => {
    if (!conversationId || !myUserId) return
    setHistoryReady(false)
    let cancelled = false
    void actions.ensureHistory(conversationId, myUserId).finally(() => {
      if (!cancelled) setHistoryReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [actions, conversationId, myUserId])

  /* --- §4.6: `↑` в пустом поле открывает правку последнего сообщения ----- */

  const editLastOwnMessage = useCallback((): boolean => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const candidate = messages[index]
      if (!candidate) continue
      if (candidate.sender_id !== myUserId) continue

      // Первое своё сообщение с конца решает исход: если его править нельзя,
      // «перепрыгивать» к более старому было бы неожиданно.
      return canEditMessage(candidate, config, myUserId)
        ? (setComposerMode({ kind: 'edit', message: candidate }), true)
        : false
    }

    return false
  }, [config, messages, myUserId])

  /* --- Прокрутка историей управляет сама лента (D-03, CC §2.6 п. 5) ------- */

  const loadOlder = useCallback(async (): Promise<boolean | void> => {
    if (!conversationId || loadingOlder) return

    setLoadingOlder(true)
    try {
      return await actions.loadOlder(conversationId, myUserId)
    } finally {
      setLoadingOlder(false)
    }
  }, [actions, conversationId, loadingOlder, myUserId])

  /*
   * D-03 §3.5 п. 5: после перестройки раскладки история открывается там же,
   * где была, — по `seq` последнего видимого сообщения, а не «в самом низу».
   */
  useLayoutEffect(() => {
    if (!conversationId || restoredRef.current) return

    const anchor = takeHistoryAnchor(conversationId)
    if (anchor === null) return

    if (historyHandleRef.current?.restoreToSeq(anchor)) restoredRef.current = true
  }, [conversationId, messages.length])

  useEffect(() => {
    restoredRef.current = false
  }, [conversationId])

  /* --- Отметка прочтения --------------------------------------------------- */

  const [visibleAnchor, setVisibleAnchor] = useState<number | null>(null)
  const lastVisibleSeq = useCallback(() => historyHandleRef.current?.lastVisibleSeq() ?? null, [])
  useReadReceipts({
    conversation,
    historyReady,
    hidden: searchOpen,
    messages,
    visibleAnchor,
    lastVisibleSeq,
    markRead: actions.markRead,
  })
  const handleAnchorChange = useCallback(
    (seq: number | null) => {
      if (conversationId) rememberHistoryAnchor(conversationId, seq)
      setVisibleAnchor(seq)
    },
    [conversationId],
  )

  /* --- Отправка -------------------------------------------------------------- */

  const handleSend = useCallback(
    async (text: string, media?: LocalMediaDraft | null) => {
      if (!conversationId) return

      if (composerMode.kind === 'edit') {
        await actions.edit(conversationId, composerMode.message, text)
        setComposerMode({ kind: 'new' })
        return
      }

      if (composerMode.kind === 'forward') {
        const source = composerMode.message
        setComposerMode({ kind: 'new' })
        await actions.send({
          conversationId,
          senderId: myUserId,
          text: '',
          forwardOf: source.id,
          // `system` сервером не создаётся (BS §7.5), в меню такого сообщения нет
          forwardType: source.type === 'system' ? 'text' : source.type,
        })
        // Комментарий к пересылке — отдельным сообщением следом, как в Telegram.
        if (text.trim()) await actions.send({ conversationId, senderId: myUserId, text })
        return
      }

      await actions.send({
        conversationId,
        senderId: myUserId,
        text,
        replyTo: composerMode.kind === 'reply' ? toReplyPreview(composerMode.message) : null,
        media: media ?? null,
      })

      setComposerMode({ kind: 'new' })
    },
    [actions, composerMode, conversationId, myUserId],
  )

  /*
   * Переход к сообщению из поиска (C-08) или из цитаты ответа. Пока открыт
   * поиск, лента скрыта и прокручивать её бессмысленно — переход откладывается
   * до закрытия панели и выполняется, когда лента снова в раскладке.
   */
  const [scrollTarget, setScrollTarget] = useState<number | null>(null)

  const scrollToSeq = useCallback((seq: number) => {
    setScrollTarget(seq)
    setSearchOpen(false)
  }, [])

  useEffect(() => {
    if (scrollTarget === null || !conversationId) return
    const controller = new AbortController()
    void actions
      .loadThrough(conversationId, myUserId, scrollTarget, controller.signal)
      .then((found) => {
        if (!controller.signal.aborted && !found) {
          setScrollTarget(null)
          toast.show('Сообщение больше недоступно в истории.')
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setScrollTarget(null)
          toast.show('Не удалось открыть сообщение. Повторите поиск.')
        }
      })
    return () => controller.abort()
  }, [actions, conversationId, myUserId, scrollTarget, toast])

  useLayoutEffect(() => {
    if (searchOpen || scrollTarget === null || !messages.some((message) => message.seq === scrollTarget))
      return
    historyHandleRef.current?.scrollToSeq(scrollTarget)
    setHighlightSeq(scrollTarget)
    setScrollTarget(null)
  }, [messages, searchOpen, scrollTarget])

  useEffect(() => {
    if (highlightSeq === null) return
    const timer = setTimeout(() => setHighlightSeq(null), 1800)
    return () => clearTimeout(timer)
  }, [highlightSeq])

  useEffect(() => setScrollTarget(null), [conversationId])

  /*
   * §4.6 п. 3, п. 5: `Ctrl/Cmd + F` — единственное браузерное сочетание,
   * которое разрешено перехватывать, и только при открытом диалоге.
   * Видимый эквивалент — кнопка поиска в шапке.
   */
  useEffect(() => {
    if (!conversationId) return

    const onKeyDown = (event: KeyboardEvent): void => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'f') return
      if (!hotkeysAvailable()) return

      event.preventDefault()
      setSearchOpen(true)
    }

    globalThis.addEventListener('keydown', onKeyDown)
    return () => globalThis.removeEventListener('keydown', onKeyDown)
  }, [conversationId])

  // Смена диалога закрывает поиск: результаты относятся к прежней переписке.
  useEffect(() => setSearchOpen(searchRoute), [conversationId, searchRoute])

  /* --- Состояния экрана ------------------------------------------------------- */

  if (!conversationId) return null

  /*
   * Загрузка и «диалог недоступен» — в той же рамке, что и сам диалог: рамка монтируется
   * сразу и въезжает со снимком списка под ней, а содержимое дорисовывается внутри.
   * Без рамки экран загрузки вставал на место без движения, а push стартовал только
   * после ответа реплики — диалог вспыхивал, пропадал и въезжал заново.
   */
  if (conversation === undefined || conversation === null) {
    return (
      <ScreenFrame
        ref={frameRef}
        nested={!embedded}
        onBack={embedded ? undefined : navigateBack}
        className={styles['chat']}
      >
        <header className={styles['header']}>
          <button
            type="button"
            className={cx(styles['headerBack'], embedded && styles['headerBackHidden'])}
            aria-label="Назад"
            onClick={goBack}
          >
            <Icon name="back" size={22} />
          </button>
        </header>
        {conversation === undefined ? (
          <div className={styles['loading']}>
            <Spinner size={28} label="Загрузка диалога" />
          </div>
        ) : (
          // SCREEN_CATALOG §15: `not_found` → SYS-15 и возврат в список.
          <EmptyState
            icon="alert"
            title="Диалог недоступен"
            text="Возможно, он был закрыт или удалён."
            actions={
              <Button variant="primary" onClick={() => void navigate({ to: '/chats' })}>
                К списку чатов
              </Button>
            }
          />
        )}
      </ScreenFrame>
    )
  }

  if (firstUnreadRef.current?.conversationId !== conversation.id) {
    firstUnreadRef.current = {
      conversationId: conversation.id,
      seq: conversation.unread_count > 0 ? conversation.my.last_read_seq + 1 : null,
    }
  }
  const firstUnreadSeq = firstUnreadRef.current.seq

  const counterpart = conversation.counterpart
  // Изменение v1.7: у ленты канала контрагента нет — имя, аватар и доступность берутся из карточки.
  const face = faceOf(conversation)
  const channel = face.channel
  const unavailable = face.availability !== 'available'
  const closed = conversation.state === 'closed'
  // В канале пишут только владелец и администраторы; остальным композер заменяет полоса подписки.
  const readerOnly = channel !== null && !channel.can_post
  const counterpartName = channel
    ? channel.title
    : unavailable
      ? face.availability === 'deleted'
        ? 'Удалённый аккаунт'
        : 'Профиль недоступен'
      : face.name

  /*
   * C-25 «Зона drag-and-drop». Счётчик глубины нужен потому, что
   * `dragleave` приходит и при переходе между вложенными элементами —
   * без него подсветка мигала бы на каждом пузыре сообщения.
   */
  const dragProps =
    closed || unavailable || readOnly
      ? {}
      : {
          onDragEnter: (event: React.DragEvent) => {
            if (!event.dataTransfer.types.includes('Files')) return
            dragDepthRef.current += 1
            setDragActive(true)
          },
          onDragOver: (event: React.DragEvent) => {
            if (!event.dataTransfer.types.includes('Files')) return
            // Без preventDefault браузер откроет файл вместо передачи его нам.
            event.preventDefault()
            event.dataTransfer.dropEffect = 'copy'
          },
          onDragLeave: () => {
            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
            if (dragDepthRef.current === 0) setDragActive(false)
          },
          onDrop: (event: React.DragEvent) => {
            event.preventDefault()
            dragDepthRef.current = 0
            setDragActive(false)
            setDroppedFile(event.dataTransfer.files[0] ?? null)
          },
        }

  return (
    <ScreenFrame
      ref={frameRef}
      nested={!embedded}
      onBack={embedded ? undefined : navigateBack}
      className={cx(styles['chat'], dragActive && styles['chatDropActive'])}
      {...dragProps}
    >
      {/* C-25: подсказка появляется только во время перетаскивания */}
      {dragActive ? (
        <div className={styles['dropHint']} aria-hidden="true">
          <Icon name="image" size={28} />
          <span>Отпустите, чтобы отправить файл</span>
        </div>
      ) : null}

      {/* --- Шапка --- */}
      <header className={styles['header']}>
        <button
          type="button"
          className={cx(styles['headerBack'], embedded && styles['headerBackHidden'])}
          aria-label="Назад"
          onClick={goBack}
        >
          <Icon name="back" size={22} />
        </button>

        <button
          type="button"
          className={styles['headerMain']}
          onClick={() => {
            if (channel) void navigate({ to: '/channels/$channelId', params: { channelId: channel.id } })
            else if (counterpart && !unavailable) {
              void navigate({ to: '/users/$userId', params: { userId: counterpart.id } })
            }
          }}
        >
          <Avatar
            name={face.name}
            photo={face.photo}
            availability={face.availability}
            size="md"
            online={counterpart?.presence?.online ?? false}
          />

          <span className={styles['headerText']}>
            <span className={styles['headerName']}>{counterpartName}</span>
            <span
              className={cx(
                styles['headerStatus'],
                typing && styles['headerStatusTyping'],
                !typing && counterpart?.presence?.online && styles['headerStatusOnline'],
              )}
            >
              {channel
                ? subscribersLabel(channel.subscribers_count, channel.format)
                : typing
                  ? 'печатает…'
                  : (formatPresence(counterpart?.presence ?? null) ??
                    (face.username && !unavailable ? `@${face.username}` : ''))}
            </span>
          </span>
        </button>

        <div className={styles['headerActions']}>
          {/* C-08: поиск по диалогу — видимый эквивалент Ctrl/Cmd + F (§4.6 п. 5) */}
          <Button
            icon="search"
            variant="ghost"
            aria-label="Поиск по диалогу"
            onClick={() => setSearchOpen(true)}
          />
          <Button
            icon="more"
            variant="ghost"
            aria-label="Действия с диалогом"
            onClick={(event) => setChatMenuAnchor(event.currentTarget.getBoundingClientRect())}
          />
        </div>
      </header>

      {/* --- C-08: поиск вместо истории, пока он открыт --- */}
      {/* Закреплённое сообщение — плашка под шапкой, как в Telegram; нажатие ведёт к сообщению */}
      {pinned && !pinned.deleted && !searchOpen ? (
        <div className={styles['pinnedBar']}>
          <button
            type="button"
            className={styles['pinnedMain']}
            onClick={() => historyHandleRef.current?.scrollToSeq(pinned.seq)}
          >
            <span className={styles['pinnedTitle']}>Закреплённое сообщение</span>
            <span className={styles['pinnedText']}>{pinned.text_preview ?? mediaLabel(pinned.type)}</span>
          </button>
          <button
            type="button"
            className={styles['pinnedClose']}
            aria-label="Открепить"
            onClick={() => void actions.pin(conversationId, null)}
          >
            <Icon name="close" size={18} />
          </button>
        </div>
      ) : null}

      {searchOpen ? (
        <ChatSearch
          conversationId={conversationId}
          myUserId={myUserId}
          onPick={scrollToSeq}
          onClose={() => setSearchOpen(false)}
        />
      ) : null}

      {/* --- История (C-07) --- */}
      <MessageHistory
        handleRef={historyHandleRef}
        messages={messages}
        myUserId={myUserId}
        counterpartName={face.name}
        senders={groupCard ? senders : undefined}
        highlightSeq={highlightSeq}
        onOpenComments={
          channel?.comments_enabled
            ? (post) =>
                void navigate({
                  to: '/chats/$conversationId/posts/$postId',
                  params: { conversationId, postId: post.id },
                })
            : undefined
        }
        loadingOlder={loadingOlder}
        hidden={searchOpen}
        onLoadOlder={loadOlder}
        onOpenMedia={(mediaId) => setViewerMediaId(mediaId)}
        onContextMenu={(target, anchor) => setMenuFor({ message: target, anchor })}
        onRetry={(target) => {
          if (target.client_message_id) void actions.retry(conversationId, target.client_message_id)
        }}
        onDiscard={(target) => {
          if (target.client_message_id) void actions.discard(conversationId, target.client_message_id)
        }}
        onOpenLink={setPendingLink}
        onReact={(target, reaction) =>
          void actions.react(conversationId, { id: target.id, seq: target.seq }, reaction, myUserId)
        }
        onReply={(target) => setComposerMode({ kind: 'reply', message: target })}
        quickReaction={config?.reactions[0] ?? null}
        onAnchorChange={handleAnchorChange}
        firstUnreadSeq={firstUnreadSeq}
        empty={
          // Только когда и реплика ответила, и первая страница истории загружена
          messagesLoaded && historyReady ? (
            <div className={styles['emptyCard']}>
              {/* Значок по форме: рупор у канала, люди у группы — иначе группа выглядела рассылкой */}
              <Icon name={channel ? spaceIcon(channel.format) : 'sparkle'} size={28} />
              <div className={styles['emptyTitle']}>
                {channel && channel.format === 'channel' ? 'Пока нет публикаций' : 'Начните разговор'}
              </div>
              <p className={styles['emptyText']}>
                {channel
                  ? channel.format === 'group'
                    ? 'Напишите первым — сообщение увидят все участники'
                    : channel.can_post
                      ? 'Опубликуйте первое сообщение — его увидят подписчики'
                      : 'Автор ещё ничего не опубликовал'
                  : 'Вы оба за движ — напишите первым'}
              </p>
            </div>
          ) : null
        }
        typing={
          typing ? (
            <div className={styles['typingRow']}>
              <span className={styles['typingDots']} aria-label="Собеседник печатает">
                <span className={styles['typingDot']} />
                <span className={styles['typingDot']} />
                <span className={styles['typingDot']} />
              </span>
            </div>
          ) : null
        }
      />

      {/* --- Композер, полоса канала или сообщение о закрытии (C-21, SYS-16, C-30) --- */}
      {readerOnly && !closed ? (
        <Suspense fallback={null}>
          <ChannelFooter channel={channel} />
        </Suspense>
      ) : closed || unavailable || readOnly ? (
        <div className={styles['closedNotice']}>
          <Icon name="lock" size={16} />
          <span>
            {/* SYS-16: при `read_only` объясняем причину, а не гасим кнопку молча */}
            {readOnly
              ? 'Отправка сообщений ограничена по решению модерации. История доступна для чтения.'
              : unavailable
                ? 'Профиль недоступен. Отправка сообщений отключена.'
                : 'Диалог закрыт. История доступна только для чтения.'}
          </span>
        </div>
      ) : (
        <Composer
          key={conversationId}
          onEditLast={editLastOwnMessage}
          droppedFile={droppedFile}
          onDroppedFileTaken={() => setDroppedFile(null)}
          conversationId={conversationId}
          mode={composerMode}
          onModeChange={setComposerMode}
          onSend={handleSend}
          counterpartName={face.name}
          myUserId={myUserId}
        />
      )}

      {/* --- SF-01 / SF-02: жалоба на сообщение или вложение --- */}
      <ReportDialog
        target={reportTarget}
        categories={config?.report_categories ?? []}
        onClose={() => setReportTarget(null)}
        onSubmitted={(reportId) => {
          setReportTarget(null)
          if (reportId) void uiStateRepo.set(UiKey.lastReportId, reportId)
          toast.show('Жалоба отправлена')
        }}
      />

      {/* --- C-22: предупреждение перед переходом по внешней ссылке --- */}
      <ExternalLinkDialog link={pendingLink} onClose={() => setPendingLink(null)} />

      {/* --- C-09: полноэкранный просмотрщик --- */}
      {viewerMediaId ? (
        <Suspense fallback={null}>
          <MediaViewer
            messages={messages}
            mediaId={viewerMediaId}
            myUserId={myUserId}
            onClose={() => setViewerMediaId(null)}
            onReport={(message) => {
              setViewerMediaId(null)
              if (message.media) {
                setReportTarget({
                  type: 'media',
                  id: message.media.id,
                  title: 'Пожаловаться на вложение',
                })
              }
            }}
            onDeleteForAll={(message) => void actions.remove(conversationId, message.id, 'all')}
          />
        </Suspense>
      ) : null}

      {/* --- C-14: действия с сообщением --- */}
      <Sheet
        open={menuFor !== null}
        onClose={() => setMenuFor(null)}
        anchorRect={menuFor?.anchor ?? null}
        title="Сообщение"
        description={menuFor?.message.text ? excerpt(menuFor.message.text) : undefined}
        flush
      >
        {/* Быстрые реакции — набор из `GET /config → reactions` (BS §7.6), своя подсвечена */}
        {menuFor && !menuFor.message.deleted && config?.reactions.length ? (
          <div className={styles['reactionBar']} role="group" aria-label="Реакция">
            {config.reactions.map((emoji) => {
              const mine = menuFor.message.reactions.some(
                (r) => r.user_id === myUserId && r.reaction === emoji,
              )
              return (
                <button
                  key={emoji}
                  type="button"
                  className={cx(styles['reactionChoice'], mine && styles['reactionChoiceActive'])}
                  aria-label={`Реакция ${emoji}`}
                  aria-pressed={mine}
                  onClick={() => {
                    const target = menuFor.message
                    setMenuFor(null)
                    void actions.react(
                      conversationId,
                      { id: target.id, seq: target.seq },
                      mine ? null : emoji,
                      myUserId,
                    )
                  }}
                >
                  {emoji}
                </button>
              )
            })}
          </div>
        ) : null}

        <ActionList>
          {/* C-19: неотправленное сообщение — сначала повтор и удаление черновика */}
          {menuFor?.message.local_state === 'failed' ? (
            <>
              <ActionItem
                icon="refresh"
                label="Повторить отправку"
                onClick={() => {
                  const target = menuFor?.message
                  setMenuFor(null)
                  if (target?.client_message_id) void actions.retry(conversationId, target.client_message_id)
                }}
              />
              <ActionItem
                icon="trash"
                label="Удалить неотправленное"
                danger
                onClick={() => {
                  const target = menuFor?.message
                  setMenuFor(null)
                  if (target?.client_message_id)
                    void actions.discard(conversationId, target.client_message_id)
                }}
              />
            </>
          ) : null}

          {menuFor?.message.local_state !== 'failed' ? (
            <ActionItem
              icon="reply"
              label="Ответить"
              onClick={() => {
                if (menuFor) setComposerMode({ kind: 'reply', message: menuFor.message })
                setMenuFor(null)
              }}
            />
          ) : null}

          {menuFor?.message.text ? (
            <ActionItem
              icon="copy"
              label="Копировать текст"
              onClick={() => {
                const text = menuFor?.message.text ?? ''
                setMenuFor(null)
                void navigator.clipboard
                  .writeText(text)
                  .then(() => toast.show('Скопировано'))
                  .catch(() => toast.show('Не удалось скопировать', { tone: 'danger' }))
              }}
            />
          ) : null}

          {/* У неотправленного сообщения ещё нет серверного id, пересылать нечего */}
          {menuFor && !menuFor.message.deleted && menuConfirmed ? (
            <ActionItem
              icon="forward"
              label="Переслать"
              onClick={() => {
                const target = menuFor?.message ?? null
                setMenuFor(null)
                setForwardFor(target)
              }}
            />
          ) : null}

          {menuFor &&
          menuConfirmed &&
          (conversation.state === 'active' || pinned?.id === menuFor.message.id) ? (
            <ActionItem
              icon="pushpin"
              label={pinned?.id === menuFor.message.id ? 'Открепить' : 'Закрепить'}
              onClick={() => {
                const target = menuFor?.message
                setMenuFor(null)
                if (target) void actions.pin(conversationId, pinned?.id === target.id ? null : target.id)
              }}
            />
          ) : null}

          {/* C-17: окно правки задано `limits.edit_window_sec` (BS §7.5) */}
          {menuFor && canEditMessage(menuFor.message, config, myUserId) ? (
            <ActionItem
              icon="edit"
              label="Изменить"
              onClick={() => {
                if (menuFor) setComposerMode({ kind: 'edit', message: menuFor.message })
                setMenuFor(null)
              }}
            />
          ) : null}

          {menuFor?.message.local_state !== 'failed' ? (
            <ActionItem
              icon="trash"
              label="Удалить у меня"
              onClick={() => {
                if (menuFor) void actions.remove(conversationId, menuFor.message.id, 'me')
                setMenuFor(null)
              }}
            />
          ) : null}

          {/* Окно «удалить у всех» — `limits.delete_all_window_sec` (BS §7.5) */}
          {menuFor && canDeleteForAll(menuFor.message, config, myUserId) ? (
            <ActionItem
              icon="trash"
              label="Удалить у всех"
              danger
              onClick={() => {
                if (menuFor) void actions.remove(conversationId, menuFor.message.id, 'all')
                setMenuFor(null)
              }}
            />
          ) : null}

          {menuFor?.message.sender_id !== myUserId ? (
            <ActionItem
              icon="flag"
              label="Пожаловаться на сообщение"
              danger
              onClick={() => {
                const target = menuFor?.message
                setMenuFor(null)
                if (target) setReportTarget(messageReportTarget(target, messages, counterpartName))
              }}
            />
          ) : null}
        </ActionList>
      </Sheet>

      <ForwardSheet message={forwardFor} excludeId={conversationId} onClose={() => setForwardFor(null)} />

      {/* --- C-05: действия с диалогом --- */}
      <Sheet
        open={chatMenuAnchor !== null}
        onClose={() => setChatMenuAnchor(null)}
        anchorRect={chatMenuAnchor}
        title={counterpartName ?? 'Диалог'}
        flush
      >
        <ActionList>
          <ActionItem
            icon={channel ? spaceIcon(channel.format) : 'user'}
            label={channel ? (channel.format === 'group' ? 'О группе' : 'О канале') : 'Открыть профиль'}
            disabled={unavailable}
            onClick={() => {
              setChatMenuAnchor(null)
              if (channel) void navigate({ to: '/channels/$channelId', params: { channelId: channel.id } })
              else if (counterpart)
                void navigate({ to: '/users/$userId', params: { userId: counterpart.id } })
            }}
          />
          <ActionItem
            icon="bell-off"
            label={conversation.my.muted_until ? 'Включить уведомления' : 'Отключить уведомления'}
            onClick={() => {
              setChatMenuAnchor(null)
              // C-06: длительность выбирает пользователь, а не код.
              if (conversation.my.muted_until) void actions.setMuted(conversationId, null)
              else setMuteOpen(true)
            }}
          />
          <ActionItem
            icon="archive"
            label={conversation.my.archived_at ? 'Убрать из архива' : 'В архив'}
            onClick={() => {
              void actions.setArchived(conversationId, conversation.my.archived_at === null)
              setChatMenuAnchor(null)
              if (!conversation.my.archived_at) void navigate({ to: '/chats' })
            }}
          />
          <ActionItem
            icon="trash"
            label="Очистить историю"
            danger
            onClick={() => {
              // ПС §04: разрушающее действие подтверждается (C-24).
              setChatMenuAnchor(null)
              setConfirm('clear')
            }}
          />
          <ActionItem
            icon="trash"
            label="Удалить чат у меня"
            danger
            onClick={() => {
              setChatMenuAnchor(null)
              setConfirm('hide')
            }}
          />
        </ActionList>
      </Sheet>

      {/* --- C-06: длительность mute --- */}
      <Sheet
        open={muteOpen}
        onClose={() => setMuteOpen(false)}
        title="Отключить уведомления"
        description="Счётчик непрочитанного останется — mute влияет только на push."
        flush
      >
        <ActionList>
          {MUTE_OPTIONS.map((option) => (
            <ActionItem
              key={option.label}
              icon="bell-off"
              label={option.label}
              onClick={() => {
                setMuteOpen(false)
                void actions.setMuted(conversationId, new Date(Date.now() + option.ms).toISOString())
              }}
            />
          ))}
        </ActionList>
      </Sheet>

      {/* --- C-24: подтверждение очистки и удаления --- */}
      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm === 'hide' ? 'Удалить чат у себя?' : 'Очистить историю?'}
        description={
          confirm === 'hide'
            ? 'Диалог исчезнет только у вас и вернётся с новым сообщением.'
            : 'Сообщения исчезнут только у вас. У собеседника переписка останется.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                const action = confirm
                setConfirm(null)
                if (action === 'clear') {
                  void actions
                    .clearHistory(conversationId)
                    .catch(() => toast.show('Не удалось очистить историю', { tone: 'danger' }))
                } else if (action === 'hide') {
                  void actions
                    .hideConversation(conversationId)
                    .then(() => navigate({ to: '/chats' }))
                    .catch(() => toast.show('Не удалось удалить чат', { tone: 'danger' }))
                }
              }}
            >
              {confirm === 'hide' ? 'Удалить' : 'Очистить'}
            </Button>
          </>
        }
      />
    </ScreenFrame>
  )
}

/** Короткая выдержка текста для описания листа действий. */
function excerpt(text: string): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  return flat.length > 90 ? `${flat.slice(0, 90)}…` : flat
}

function toReplyPreview(message: LocalMessage): Message['reply_to'] {
  return {
    id: message.id,
    seq: message.seq,
    sender: message.sender ?? null,
    sender_id: message.sender_id ?? '',
    type: 'text',
    text_preview: message.text?.slice(0, 120) ?? null,
    deleted: message.deleted,
  }
}
